import { type PartialDeep } from 'type-fest';

import { type CanvasElementFrom } from '@blaze-runtime/engine-domain';
import { type BlazeSchema } from '@blaze-shared/schema-domain';

import { type DefinitionMapper, type ResolvedChildDefinition } from '../../../ai';
import { toWidthStyle } from '../../../ai/models/styles';
import { type CanvasElementDefinition } from '../../../canvas-elements/element-definition';

import {
  toAlignmentStyle,
  toIconPositionStyle,
  toTabSizeStyle,
  toTemplateStyle,
} from './tabs.appearance';
import { type Tab, type TabsDefinition } from './tabs.definition';

export class TabsDefinitionMapper implements DefinitionMapper<'tabs'> {
  readonly type = 'tabs';

  assembleChildren(
    definition: PartialDeep<TabsDefinition>,
    slotChildren: ResolvedChildDefinition[][],
  ): void {
    slotChildren.forEach((children, index) => {
      const tab = definition.tabs?.[index];
      if (children.length && tab) {
        tab.children = children as unknown as Tab['children'];
      }
    });
  }

  extract(
    element: CanvasElementFrom<'tabs'>,
    _schemas: Readonly<Record<string, BlazeSchema>>,
    definition: CanvasElementDefinition<'tabs'>,
  ): PartialDeep<TabsDefinition> {
    const content = element.props.content;
    const options = content?.options;
    const defaultOptions = definition.defaults?.content.options;

    const template = options?.template ?? defaultOptions?.template;
    const tabSize = options?.tabSize ?? defaultOptions?.tabSize;
    const tabWidth = options?.tabWidth ?? defaultOptions?.tabWidth;
    const alignment = options?.align ?? defaultOptions?.align;
    const iconPosition = options?.iconPosition ?? defaultOptions?.iconPosition;

    const tabs: Tab[] = content.tabs.items.map((item) => ({
      label: item.label,
      iconName: item.icon,
      children: [],
    }));

    return {
      template: template ? toTemplateStyle(template) : undefined,
      tabSize: tabSize && tabWidth ? toTabSizeStyle(tabSize, tabWidth) : undefined,
      alignment: alignment ? toAlignmentStyle(alignment) : undefined,
      iconPosition: iconPosition ? toIconPositionStyle(iconPosition) : undefined,
      width: toWidthStyle(element.width),
      tabs,
    };
  }
}
